import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode, errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { WBSButton } from '@/common/WBSButton';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { ModalParagraph, StudentBoardModal } from './StudentBoardModal';

interface EncourageStudentModalProps {
  /** The student to encourage. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  /**
   * The board row's own name. Encourage has no `/adoption-info` step to read it
   * from; that endpoint runs the adopt checks and would refuse a helper who may
   * encourage but not adopt.
   */
  studentName: string;
  onClose: () => void;
  /** Fired once the encouragement has landed, so the board can refresh behind it. */
  onEncouraged?: () => void;
}

/**
 * The two-step encourage popup: the message form, then the sent body.
 *
 * The field is a rich text editor, as in legacy, so the stored body is HTML and
 * the 1500 limit is on its plain text. There's no counter or client-side length
 * check, since the browser can't measure that off the markup.
 *
 * A refusal re-renders the form with a notice above it, so the typed message
 * survives.
 */
export function EncourageStudentModal({
  studentId,
  studentName,
  onClose,
  onEncouraged,
}: EncourageStudentModalProps) {
  const { t } = useTranslation();
  const editor = useRichTextEditor();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [showEmptyError, setShowEmptyError] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing now, readable from a settled request;
  // see `AdoptStudentModal`.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late,
  // and a second send would come back 409 `student_already_encouraged`.
  const submitting = useRef(false);

  const { mutateAsync: sendEncouragement, isPending } =
    api.sendEncouragement.useMutation();
  const submitErrorMessage = useEncouragementErrorMessage(submitError);

  // Reopening for a different student must not inherit the last one's sent body
  // or error. Adjusting state during render (rather than in an effect) keeps the
  // first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setSentTo(null);
    setShowEmptyError(false);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  if (studentId === null) return null;

  const handleClose = (): void => {
    editor.clearEditor();
    // The student STAYS on the board after an encouragement — unlike adopt and
    // pass-on — so the same row can be clicked again before the board refetches.
    // Without this reset that second open would paint the previous sent body.
    setSentTo(null);
    setShowEmptyError(false);
    setSubmitError(null);
    onClose();
  };

  // Sent first: the encouragement has landed, so nothing that follows can undo
  // it. Held as the name it was sent to rather than a bool, so the sentence
  // still names the right student if the row list refreshes underneath.
  if (sentTo !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.student-encouraged')}
        onClose={handleClose}
        footer={
          <WBSButton alternate onClick={handleClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.your-encouragement-has-been-sent', { name: sentTo })}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  const handleSend = async (): Promise<void> => {
    if (submitting.current) return;
    // The plain-text form of the field: a focused but empty contenteditable
    // holds `<div><br></div>`, so testing the markup would pass an empty
    // message to the server.
    if (!editor.getEditorContent()) {
      setShowEmptyError(true);
      setSubmitError(null);
      return;
    }
    submitting.current = true;
    const submittedFor = studentId;
    const submittedName = studentName;
    setShowEmptyError(false);
    setSubmitError(null);
    try {
      await sendEncouragement({
        body: {
          student_id: studentId,
          // The markup, not the text: formatting is the point of the control,
          // and the server stores the formatted body.
          message: editor.editorRef.current?.innerHTML ?? '',
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onEncouraged?.();
    if (openSession.current === submittedFor) setSentTo(submittedName);
  };

  return (
    <StudentBoardModal
      title={t('wbs.encourage-this-student')}
      onClose={handleClose}
      footer={
        <WBSButton
          onClick={() => {
            void handleSend();
          }}
          isLoading={isPending}
        >
          {t('wbs.send')}
        </WBSButton>
      }
    >
      {showEmptyError && (
        <AlertBox variant="error">
          {t('wbs.please-enter-an-encouragement')}
        </AlertBox>
      )}
      {submitErrorMessage !== null && (
        <AlertBox variant="error">{submitErrorMessage}</AlertBox>
      )}
      <FieldLabel htmlFor="encouragement_message">
        {t('wbs.message-of-encouragement')}
      </FieldLabel>
      {/* Legacy's field id and toolbar buttons. */}
      <BorderedRichTextEditor
        editor={editor}
        id="encouragement_message"
        visibleTools={['bold', 'italic', 'underline', 'blockquote']}
      />
    </StudentBoardModal>
  );
}

/**
 * The server's refusals, in legacy's words where it has them.
 *
 * The empty and too-long messages are legacy's validation notices; the two 409s
 * are the button's disabled states as prose, for a row whose label was computed
 * before someone else's send landed.
 */
function useEncouragementErrorMessage(error: unknown): string | null {
  const { t } = useTranslation();

  if (error === null) return null;

  switch (errorCode(error)) {
    case 'encouragement_message_empty':
      return t('wbs.please-enter-an-encouragement');
    case 'encouragement_message_too_long':
      return t('wbs.encourage-message-cannot-be');
    case 'student_already_encouraged':
      return t('profile.you-already-sent-encouragement');
    case 'student_fully_encouraged':
      return t('wbs.maximum-encouragement-limit-reached');
    case 'student_already_adopted':
      return t('wbs.sorry-this-student-has-already-been-adopted');
    default:
      // `student_not_encourageable` and the 404 have no legacy wording (legacy
      // hides the button instead), so they get the generic message.
      return errorMessage(error, t('wbs.something-went-wrong-please'));
  }
}

const FieldLabel = styled.label`
  color: var(--grey-900);
  display: block;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;
